import { createContext, useEffect, useReducer, useState } from 'react';
import {
  ChildrenType,
  NotificationInboxResponse,
  PendingRequestProps,
  Product,
  ROLE,
  UserContextType,
  UserDetailType,
} from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import { userDetails } from '../utils/userDetail';
import { pendingRequest, checkeRxStatus, getUserInfo } from '../service';
import instance from '../config/axios';
import { stringify } from 'qs';
import Loading from '../components/Loading';
import { publicUrls } from '../constant/constant';
import { getUnreadNotifications } from '../service/getUnreadNotifications';
import { getAllProducts } from '../service/getAllProducts';

const UserContext = createContext({} as UserContextType);

const UserContextProvider = ({ children }: ChildrenType) => {
  const { user } = useAuth();
  const auth = useAuth();
  const [userDetail, setUserDetails] = useState<UserDetailType>();
  const [hasTokenRecords, setHasTokenRecords] = useState<boolean>(false);
  const refreshUserData = (state: number) => {
    return state + 1;
  };
  const [refreshUser, setRefreshCounter] = useReducer(refreshUserData, 0);
  const [loading, setLoadingStatus] = useState<boolean>(false);
  const [iseRxDown, seteRxDown] = useState<boolean>(false);
  const [requestPending, setRequestPending] = useState<PendingRequestProps>({
    cancelOrderRequested: false,
    patientDeactivateRequested: false,
  });

  const [loadingEnable, setLoadingEnable] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [nativeView, setNativeView] = useState<boolean>(false);
  const [hideDropdown, setHideDropdown] = useState<boolean>(true);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] =
    useState<boolean>(false);
  const [activeItem, setActiveItem] = useState<string>('');
  const [inboxItem, setInboxItem] = useState<
    Array<NotificationInboxResponse> | []
  >([]);
  const [unReadItems, setUnReadItems] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchProducts, setSearchProducts] = useState<Array<Product> | []>([]);

  const isPublicUrl = () => {
    return publicUrls.some((publicUrl) =>
      window.location.pathname.startsWith(publicUrl),
    );
  };

  useEffect(() => {
    setLoadingStatus(true);

    setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    setNativeView(!!(window as any)?.ReactNativeWebView);

    setActiveItem('Home');

    if (user?.access_token) {
      const isPatient = (auth.user?.profile?.groups as Array<string>)?.includes(
        ROLE.Patients,
      );
      {
        isPatient &&
          !!(window as any)?.ReactNativeWebView &&
          getUnreadNotifications(user?.access_token).then((res) => {
            if (typeof res === 'number') {
              setInboxItem([]);
              setUnReadItems(false);
              return;
            }

            const inboxData: NotificationInboxResponse[] = res?.data ?? [];

            setInboxItem(inboxData);

            if (inboxData?.length) {
              const unreadItems = inboxData?.filter((msg) => !msg.is_opened);

              setUnReadItems(unreadItems?.length > 0);
            }
          });

        isPatient &&
          getAllProducts(user?.access_token).then((res) => {
            if (typeof res === 'number') {
              setSearchProducts([]);
              return;
            }

            setSearchProducts(res?.data);
          });
      }

      (auth.user?.profile?.groups as Array<string>)?.includes(ROLE.Patients) &&
        getAllProducts(user?.access_token).then((res) => {
          if (typeof res === 'number') {
            setSearchProducts([]);
            return;
          }

          setSearchProducts(res?.data);
        });

      userDetails(user.access_token)
        .then((res): void => {
          const nameParts = res.name.trim().split(/\s+/);
          getUserInfo(user?.access_token).then(async (data): Promise<void> => {
            if (typeof data === 'number') {
              setUserDetails({
                name: res.name,
                role: res.role,
                email: res.email,
                user_initial: (
                  nameParts[0][0] + nameParts[nameParts?.length - 1][0]
                ).toUpperCase(),
                first_name: res.name.split(' ')[0],
                last_name: res.name.split(' ')[1],
              });
              setLoadingStatus(false);
              return;
            }

            setUserDetails({
              id: data?.id,
              name: res.name,
              preferred_name: data?.user?.preferred_name,
              role: res.role,
              email: res.email,
              user_initial: (
                nameParts[0][0] + nameParts[nameParts?.length - 1][0]
              ).toUpperCase(),
              first_name: res.name.split(' ')[0],
              last_name: res.name.split(' ')[1],
              internal: !!data?.internal,
              supportingParams: {
                isUserAllowedToOrder: data?.allowedToOrder,
                hasValidEmail: data?.hasValidMX,
              },
            });
          });

          setLoadingStatus(false);

          if ([ROLE.Patients, ROLE.Doctors].includes(res.role)) return;

          const fetchTokens = () => {
            instance
              .get(`api/v1/tokens?${stringify({ page: 0, limit: 50 })}`, {
                headers: {
                  Authorization: `Bearer ${user.access_token}`,
                },
              })
              .then((response) => {
                if (response.status === 200) {
                  setHasTokenRecords(!!response?.data?.data?.length);
                }
              })
              .catch((error) => {
                console.error('Error fetching tokens:', error);
              });
          };

          const eRxStatus = () => {
            checkeRxStatus(user.access_token)
              .then((res) => {
                if (res.status === 200) {
                  seteRxDown(false);
                } else {
                  seteRxDown(true);
                }
              })
              .catch((error) =>
                console.error('Error check eRx status:', error),
              );
          };

          const pendingRequestExist = () => {
            pendingRequest(user.access_token)
              .then((res) => {
                if (res.status === 200) {
                  const { cancelOrderRequested, patientDeactivateRequested } =
                    res.data;
                  setRequestPending({
                    cancelOrderRequested: cancelOrderRequested,
                    patientDeactivateRequested: patientDeactivateRequested,
                  });
                }
              })
              .catch((error) =>
                console.error('Error check eRx status:', error),
              );
          };

          fetchTokens();
          eRxStatus();
          pendingRequestExist();

          setInterval(() => {
            fetchTokens();
            eRxStatus();
            pendingRequestExist();
          }, 5000);
        })
        .catch((err) => {
          console.error(err);
          auth.signoutSilent();
        });
    }
  }, [user, refreshUser]);

  if (loading && !isPublicUrl()) return <Loading />;

  return (
    <UserContext.Provider
      value={{
        userDetail: userDetail as UserDetailType,
        hasTokenRecords,
        refreshUserData: setRefreshCounter,
        refreshCounts: refreshUser,
        iseRxDown,
        requestPending,
        loadingEnable,
        setLoadingEnable,
        isMobile,
        setIsMobile,
        hideDropdown,
        setHideDropdown,
        activeItem,
        setActiveItem,
        isMobileDropdownOpen,
        setIsMobileDropdownOpen,
        nativeView,
        setNativeView,
        inboxItem,
        setInboxItem,
        unReadItems,
        setUnReadItems,
        searchTerm,
        setSearchTerm,
        searchProducts,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserContext, UserContextProvider };
